import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import { api } from '../../lib/api';
import DateInput from '../ui/DateInput';

// TaskFormDialog — rich create/edit form for tasks (item 1.9).
//
// Field set: title, description, assignee (dropdown of staff users), due date,
// priority (low/normal/high/urgent), status, related entity (free-form for
// Sprint B; the picker UI ships when more entity-specific shapes ship in C/E),
// reminder cadence (multi-select of canonical keys).
//
// On submit the parent receives the saved task (POST returns 201 with task).
// Edit mode passes `initial` and the dialog issues PATCH /api/admin/tasks/:id.

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];
const STATUS_OPTIONS = [
  { value: 'todo', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'done', label: 'Done' },
];
const REMINDER_CADENCE_OPTIONS = [
  { value: '24h_before', label: '24h before due' },
  { value: 'day_of', label: 'Day of due' },
  { value: 'overdue_3d', label: 'Overdue every 3d' },
];
const RELATED_ENTITY_TYPES = [
  { value: '', label: 'None' },
  { value: 'project', label: 'Project' },
  { value: 'audit', label: 'Audit' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'interaction', label: 'Interaction' },
  { value: 'account', label: 'Account' },
];

const inputCls =
  'w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-gray-600';
const labelCls = 'block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5';

// `accountId` prop pre-pins the new task to a specific account (used by the
// per-account TasksTab so the resulting POST gets the right account_id and
// the task surfaces under that account everywhere). For the dashboard
// "+ New Task" entrypoint this stays null.
export default function TaskFormDialog({ open, initial = null, onClose, onSaved, accountId = null }) {
  const isEdit = !!initial?.id;
  const [users, setUsers] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeUserId, setAssigneeUserId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('normal');
  const [status, setStatus] = useState('todo');
  const [relatedEntityType, setRelatedEntityType] = useState('');
  const [relatedEntityId, setRelatedEntityId] = useState('');
  const [reminders, setReminders] = useState(['24h_before', 'day_of']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState({});

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || '');
    setDescription(initial?.description || '');
    setAssigneeUserId(initial?.assigneeUserId || '');
    setDueDate(initial?.dueDate || '');
    setPriority(initial?.priority || 'normal');
    setStatus(initial?.status || 'todo');
    setRelatedEntityType(initial?.relatedEntityType || '');
    setRelatedEntityId(initial?.relatedEntityId || '');
    try {
      const parsed = initial?.reminderCadenceJson ? JSON.parse(initial.reminderCadenceJson) : ['24h_before', 'day_of'];
      setReminders(Array.isArray(parsed) ? parsed : []);
    } catch { setReminders([]); }
    setError('');
    setFieldError({});
  }, [open, initial]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        // include_admins=true so ops actors see admin users in the
        // assignee dropdown (the user-management filter that hides admins
        // is opt-out for assignment contexts).
        const res = await api.get('/api/admin/users?include_admins=true');
        if (cancelled) return;
        // Media staff can be assignees too (shoot prep, deliveries); they see their tasks on the media dashboard.
        const staff = (res.users || []).filter((u) => (u.role === 'nexsav_admin' || u.role === 'ops' || u.role === 'media') && u.isActive !== false);
        setUsers(staff);
      } catch (err) {
        // non-fatal — allows the dialog to render with assignee = unassigned
        console.error('user list load failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const toggleReminder = (key) => {
    setReminders((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setFieldError({});
    if (!title.trim()) { setFieldError({ title: 'Title is required' }); return; }
    setSubmitting(true);
    const payload = {
      title: title.trim(),
      description: description.trim() || undefined,
      assigneeUserId: assigneeUserId || undefined,
      dueDate: dueDate || undefined,
      priority,
      status,
      relatedEntityType: relatedEntityType || undefined,
      relatedEntityId: (relatedEntityType && relatedEntityId.trim()) ? relatedEntityId.trim() : undefined,
      reminderCadenceJson: JSON.stringify(reminders),
      // Only send accountId on create — edit doesn't change account scope.
      ...(accountId && !isEdit ? { accountId } : {}),
    };
    try {
      const res = isEdit
        ? await api.put(`/api/admin/tasks/${initial.id}`, payload)
        : await api.post('/api/admin/tasks', payload);
      onSaved?.(res?.task);
      onClose?.();
    } catch (err) {
      if (err?.data?.fieldErrors) setFieldError(err.data.fieldErrors);
      setError(err?.data?.error || err?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title={isEdit ? 'Edit task' : 'New task'}
      maxWidth="max-w-xl"
      footer={
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} disabled={submitting}
            className="px-4 py-2 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white">
            Cancel
          </button>
          <button type="submit" form="task-form" disabled={submitting || !title.trim()}
            data-testid="task-form-submit"
            className="px-5 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] disabled:opacity-50">
            {submitting ? 'Saving…' : (isEdit ? 'Save' : 'Create task')}
          </button>
        </div>
      }
    >
      <form id="task-form" onSubmit={submit} className="space-y-3">
        {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-2">{error}</div>}
        <label className="block">
          <span className={labelCls}>Title <span className="text-red-400/80">*</span></span>
          <input data-testid="task-title" value={title} onChange={(e) => setTitle(e.target.value)}
            className={inputCls} placeholder="What needs doing?" />
          {fieldError.title && <p className="mt-1 text-[11px] text-red-400">{fieldError.title}</p>}
        </label>
        <label className="block">
          <span className={labelCls}>Description</span>
          <textarea data-testid="task-description" rows={3} value={description}
            onChange={(e) => setDescription(e.target.value)} className={inputCls}
            placeholder="Context, links, etc." />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className={labelCls}>Assignee</span>
            <select data-testid="task-assignee" value={assigneeUserId}
              onChange={(e) => setAssigneeUserId(e.target.value)} className={inputCls}>
              <option value="">Unassigned</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={labelCls}>Due date</span>
            <DateInput data-testid="task-due-date" value={dueDate}
              onChange={setDueDate} className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Priority</span>
            <select data-testid="task-priority" value={priority}
              onChange={(e) => setPriority(e.target.value)} className={inputCls}>
              {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className={labelCls}>Status</span>
            <select data-testid="task-status" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className={labelCls}>Related to</span>
            <select data-testid="task-related-type" value={relatedEntityType}
              onChange={(e) => setRelatedEntityType(e.target.value)} className={inputCls}>
              {RELATED_ENTITY_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          {relatedEntityType && (
            <label className="block">
              <span className={labelCls}>{relatedEntityType} id</span>
              <input data-testid="task-related-id" value={relatedEntityId}
                onChange={(e) => setRelatedEntityId(e.target.value)} className={inputCls}
                placeholder={`${relatedEntityType} id`} />
            </label>
          )}
        </div>
        <div>
          <span className={labelCls}>Reminders</span>
          <div className="flex flex-wrap items-center gap-3">
            {REMINDER_CADENCE_OPTIONS.map((o) => (
              <label key={o.value} className="inline-flex items-center gap-1.5 text-[13px] text-gray-200">
                <input type="checkbox" checked={reminders.includes(o.value)}
                  onChange={() => toggleReminder(o.value)} className="accent-[#B0FC38]"
                  data-testid={`reminder-${o.value}`} />
                {o.label}
              </label>
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
}
